import React, { useEffect, useMemo, useState } from "react";
import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import {
    getBusinessById,
    getEmployeeByBusiness,
    getTipsByBusiness,
} from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";

const getTimestampDate = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
    const date = getTimestampDate(value);
    return date ? date.toLocaleString() : "-";
};

const StarIcon = ({ filled }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#e8a20e" : "none"} stroke={filled ? "#e8a20e" : "#d4d4d4"} strokeWidth="2">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

export default function BusinessTipsV2() {
    const { userData } = useAuth();
    const [business, setBusiness] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [tips, setTips] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID) return;
            setIsLoading(true);

            const [businessData, employeeData, tipsData] = await Promise.all([
                getBusinessById(userData.businessID),
                getEmployeeByBusiness(userData.businessID),
                getTipsByBusiness(userData.businessID),
            ]);

            setBusiness(businessData || null);
            setEmployees(Array.isArray(employeeData) ? employeeData : []);
            setTips(Array.isArray(tipsData) ? tipsData : []);
            setIsLoading(false);
        };

        fetchData();
    }, [userData]);

    const sortedTips = useMemo(
        () => [...tips].sort((a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)),
        [tips]
    );

    const getEmployeeName = (id) => {
        const emp = employees.find((employee) => employee.id === id);
        return emp?.displayName || (id === "team" ? "Team Tip Pool" : "Unknown");
    };

    const totalAmount = sortedTips.reduce((sum, tip) => sum + Number(tip.amount || 0), 0);
    const ratedTips = sortedTips.filter((tip) => Number.isFinite(Number(tip.rating)));
    const avgRating = ratedTips.length
        ? ratedTips.reduce((sum, tip) => sum + Number(tip.rating), 0) / ratedTips.length
        : 0;

    return (
        <BusinessLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

                .biz-tips-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .biz-tips-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --green: #0d7c5f;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    color: var(--text-primary);
                    width: 100%;
                    padding: 32px 0 80px;
                }
                .biz-tips-v2 .header { margin-bottom: 26px; }
                .biz-tips-v2 .title { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; }
                .biz-tips-v2 .subtitle { margin-top: 4px; font-size: 14px; color: var(--text-secondary); }
                .biz-tips-v2 .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 28px;
                }
                @media (max-width: 700px) {
                    .biz-tips-v2 .stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                .biz-tips-v2 .stat-card {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .biz-tips-v2 .stat-label { font-size: 12px; font-weight: 500; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.7px; }
                .biz-tips-v2 .stat-value { font-size: 24px; font-weight: 700; letter-spacing: -0.8px; font-family: var(--mono); color: var(--green); }
                .biz-tips-v2 .stat-suffix { font-size: 12px; color: var(--text-muted); }
                .biz-tips-v2 .list-shell { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; }
                .biz-tips-v2 .list-header { background: var(--surface-raised); border-bottom: 1px solid var(--border); padding: 14px 18px; font-size: 13px; font-weight: 600; color: var(--text-secondary); }
                .biz-tips-v2 .tip-item {
                    padding: 16px 18px;
                    border-bottom: 1px solid var(--border);
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 12px;
                }
                .biz-tips-v2 .tip-item:last-child { border-bottom: none; }
                .biz-tips-v2 .left { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
                .biz-tips-v2 .amount { font-family: var(--mono); font-weight: 700; font-size: 20px; letter-spacing: -0.6px; color: var(--green); }
                .biz-tips-v2 .feedback { color: var(--text-secondary); font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 460px; }
                .biz-tips-v2 .employee { color: #4b5563; font-size: 13px; font-weight: 500; }
                .biz-tips-v2 .right { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
                .biz-tips-v2 .date { font-size: 12px; color: var(--text-muted); text-align: right; }
                .biz-tips-v2 .stars { display: flex; gap: 2px; }
                .biz-tips-v2 .loading-wrap,
                .biz-tips-v2 .empty {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    min-height: 140px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    color: var(--text-secondary);
                    font-size: 14px;
                }
                .biz-tips-v2 .spinner {
                    width: 28px;
                    height: 28px;
                    border: 3px solid #dbe8e2;
                    border-top-color: var(--accent);
                    border-radius: 9999px;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                @media (max-width: 700px) {
                    .biz-tips-v2 .tip-item { flex-direction: column; align-items: flex-start; }
                    .biz-tips-v2 .right { align-items: flex-start; }
                    .biz-tips-v2 .date { text-align: left; }
                    .biz-tips-v2 .feedback { white-space: normal; }
                }
            `}</style>

            <div className="biz-tips-v2">
                <header className="header">
                    <h1 className="title">Business Tips</h1>
                    <p className="subtitle">{business?.name || userData?.businessID || "Business"} · All tips</p>
                </header>

                <div className="stats-grid">
                    <div className="stat-card">
                        <span className="stat-label">Total Tips</span>
                        <span className="stat-value">{sortedTips.length}</span>
                        <span className="stat-suffix">transactions</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Total Amount</span>
                        <span className="stat-value">€{totalAmount.toFixed(2)}</span>
                        <span className="stat-suffix">all-time</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Avg Tip</span>
                        <span className="stat-value">€{sortedTips.length ? (totalAmount / sortedTips.length).toFixed(2) : "0.00"}</span>
                        <span className="stat-suffix">per tip</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Avg Rating</span>
                        <span className="stat-value">{avgRating ? avgRating.toFixed(1) : "0.0"}/5</span>
                        <span className="stat-suffix">rated tips only</span>
                    </div>
                </div>

                {isLoading ? (
                    <div className="loading-wrap"><div className="spinner" /></div>
                ) : sortedTips.length === 0 ? (
                    <div className="empty">No tips found.</div>
                ) : (
                    <div className="list-shell">
                        <div className="list-header">Tip History</div>
                        {sortedTips.map((tip) => (
                            <article key={tip.id} className="tip-item">
                                <div className="left">
                                    <span className="amount">+€{Number(tip.amount || 0).toFixed(2)}</span>
                                    <span className="feedback">{tip.feedback || "No feedback"}</span>
                                    <span className="employee">👤 {getEmployeeName(tip.employeeID)}</span>
                                </div>

                                <div className="right">
                                    <div className="stars">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <StarIcon key={star} filled={star <= Number(tip.rating || 0)} />
                                        ))}
                                    </div>
                                    <span className="date">{formatDateTime(tip.createdAt)}</span>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </BusinessLayoutV2>
    );
}
